import { getTheme, themed, Box, Flow, FloatedImage, getDeviceType, Tooltip, Text as Tx, } from "@bracket/ui";
import type { FunctionComponent, PropsWithChildren, } from "react";
import styled from "styled-components";



export declare type PanelItemProps = {
	label: string;
	value?: string | number;
	tip?: string;
	disabled?: true;
	type?: keyof typeof PanelType;
	fontType?: "h5" | "h6";
	color?: string;
	direction?: "row" | "column";
	gap?: string;
	logo?: string;
	logoColor?: string;
	mobileLogoCenter?: boolean;
	mobileLabelLeftPad?: string;
};


export const 	PanelItem: FunctionComponent<PropsWithChildren<PanelItemProps>> = ({
	label,
	value,
	tip,
	disabled,
	type = "NORMAL",
	fontType = "h6",
	children,
	color,
	direction = "column",
	logo,
	mobileLogoCenter,
	mobileLabelLeftPad,
}) => {
	themed();

	if(disabled)
		return null;

	const DynPanel = type === "NORMAL" ? Paneled : type === "TRANSPARENT" ? PanelTransparent : PaneledErrored;

	return (
		<DynPanel>
			{ logo && getDeviceType() !== "MOBILE" ? <FloatedImage url={logo} top="1rem" right="1rem" width="20px" height="20px" /> : null }
			{ logo && getDeviceType() === "MOBILE" ? (
				<FloatedImage
					url={logo}
					left="0rem"
					width="15px"
					height="15px"
					{...(
						mobileLogoCenter
							? { top: "50%", style: { transform: "translateY(-50%)", }, }
							: { top: "1rem", }
					)}
				/>
			) : null }
			<LabelWrapper $padLeft={logo && mobileLogoCenter ? (mobileLabelLeftPad || "1.5rem") : undefined}>
				<Tx md color={color}>{label}{tip && <Tooltip message={tip} /> }</Tx>
			</LabelWrapper>
			<PanelValue value={value} variant={fontType} direction={direction}>
				{children}
			</PanelValue>
		</DynPanel>
	);
};

export declare type PanelValueProps = PropsWithChildren<{
	variant: "h5" | "h6";
	value?: string | number | FunctionComponent;
	direction?: "row" | "column";
	gap?: string;
}>;

const PanelValue: FunctionComponent<PanelValueProps> = ({
	value,
	variant,
	children,
	direction = "column",
	gap = "12px",
}) => {
	if(typeof value === "string" || typeof value === "number") {
		return <Tx size={variant}>{value}</Tx>;
	}
	return (
		<ChildBrace $direction={direction} $gap={gap}>
			{children}
		</ChildBrace>
	);
};

const ChildBrace = styled(Box)<{ $direction: "row" | "column"; $gap: string; }>`
	flex-direction: ${props => props.$direction};
	gap: ${props => props.$gap || "12px"};
	${props => props.$direction === "row" && "align-items: center;"}
`;




const LabelWrapper = styled(Box)<{ $padLeft?: string; }>`
	@media (max-width: 1024px) {
		padding-left: ${props => props.$padLeft || 0};
		align-items: center;
	}
`;

const Paneled = styled(Flow)<{ $logo?: string; $logoColor?: string; }>`
	position: relative;
	width: 100%;
	background-color: ${() => getTheme().surface_container_lowest};
	border-radius: 8px;
	gap: 12px;
	padding: 15px;
	margin: 0.25rem;
	background-image: url(${props => props.$logo});
	height: 100%;
	// mobile exclusive below.
	@media (max-width: 1024px) {
		flex-direction: row;
		justify-content: space-between;
	}
`;

const PanelTransparent = styled(Flow)<{ $logo?: string; $logoColor?: string; }>`
	width: 100%;
	border-radius: 8px;
	gap: 12px;
	@media (max-width: 1024px) {
		padding: 0px;
		padding-top: 12px;
	}
	margin-top: 50px;
`;

const PaneledErrored = styled(Flow)`
	width: 100%;
	background-color: red;
	border-radius: 8px;
	gap: 12px;
	padding: 25px;
	margin: 0.25rem;
`;

const PanelType = {
	NORMAL: Paneled,
	TRANSPARENT: PanelTransparent,
};
